import { useCallback, useRef } from 'react'
import { track } from '../utils/analytics'

const MILESTONES = [25, 50, 75, 100] as const

// Callback ref so tracking re-attaches when a conditionally-rendered container mounts.
export function useScrollDepth(context: string): (el: HTMLElement | null) => void {
  const firedRef = useRef(new Set<number>())
  const cleanupRef = useRef<() => void>(() => {})

  return useCallback(
    (el: HTMLElement | null) => {
      cleanupRef.current()

      if (!el) return

      firedRef.current = new Set()

      const checkDepth = () => {
        let pct: number

        if (el.scrollHeight > el.clientHeight) {
          // Internal overflow scroll: measure how far the user has scrolled inside the element.
          const seen = el.scrollTop + el.clientHeight
          pct = Math.round((seen / el.scrollHeight) * 100)
        } else {
          const rect = el.getBoundingClientRect()
          if (rect.height === 0) return
          const scrolledPast = Math.max(0, -rect.top)
          const visible = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0))
          const seen = Math.min(scrolledPast + visible, rect.height)
          pct = Math.round((seen / rect.height) * 100)
        }

        for (const milestone of MILESTONES) {
          if (pct >= milestone && !firedRef.current.has(milestone)) {
            firedRef.current.add(milestone)
            track('consent.scroll_depth', { context, depth_pct: milestone })
          }
        }
      }

      checkDepth()
      el.addEventListener('scroll', checkDepth, { passive: true })
      window.addEventListener('scroll', checkDepth, { passive: true })
      cleanupRef.current = () => {
        el.removeEventListener('scroll', checkDepth)
        window.removeEventListener('scroll', checkDepth)
      }
    },
    [context]
  )
}
